import { notifications } from "@mantine/notifications";
import { useCallback, useContext, useMemo, useRef, useState, type PropsWithChildren } from "react";

import { NoticePageVisibilityType, type CurrentNotice } from "src/api/Tmd.dtos";
import LayoutContext from "src/contexts/layouts/LayoutContext";
import type { ILayoutItemContext } from "src/contexts/layouts/LayoutContext";
import NoticeContext from "src/contexts/notices/NoticeContext";
import useCurrentNotices from "src/hooks/data/load/useCurrentNotices";
import useAcknowledgeNotice from "src/hooks/data/useAcknowledgeNotice";

/**
 * Chooses the first defined notice scope in patient, trial-institution, trial, institution,
 * group and personnel order.
 *
 * Postconditions: Returns at most one ID, including zero when supplied. Department-only or empty
 * contexts return an empty object.
 */
export const deriveNoticePageContext = (context: ILayoutItemContext): ILayoutItemContext => {
    if (context.patientId !== undefined) {
        return { patientId: context.patientId };
    }
    if (context.trialInstitutionId !== undefined) {
        return { trialInstitutionId: context.trialInstitutionId };
    }
    if (context.trialId !== undefined) {
        return { trialId: context.trialId };
    }
    if (context.institutionId !== undefined) {
        return { institutionId: context.institutionId };
    }
    if (context.groupId !== undefined) {
        return { groupId: context.groupId };
    }
    if (context.personnelId !== undefined) {
        return { personnelId: context.personnelId };
    }

    return {};
};

/**
 * Shares page notices, locally dismissed IDs and acknowledgement actions with descendants.
 *
 * Postconditions: Available response data replaces the remembered global notices. While data is
 * absent, those global notices remain available, filtered by this mounted provider's dismissed IDs.
 * Loading state is published without hiding children.
 */
const NoticeProvider = ({ children }: PropsWithChildren) => {
    const layout = useContext(LayoutContext);
    const pageContext = useMemo(
        () => deriveNoticePageContext(layout.context),
        [
            layout.context.patientId,
            layout.context.trialInstitutionId,
            layout.context.trialId,
            layout.context.institutionId,
            layout.context.groupId,
            layout.context.personnelId
        ]
    );
    const response = useCurrentNotices(pageContext);
    const acknowledgeNotice = useAcknowledgeNotice(pageContext);
    const [dismissedNoticeIds, setDismissedNoticeIds] =
        useState<ReadonlySet<number>>(() => new Set());
    const globalNotices = useRef<CurrentNotice[]>([]);

    // Retain only all-page notices while the next page's response is unavailable.
    if (response.data) {
        globalNotices.current = response.data.filter(notice =>
            notice.pageVisibility === NoticePageVisibilityType.AllPages
        );
    }

    const notices = useMemo(
        () =>
            (response.data ?? globalNotices.current).filter(notice =>
                !dismissedNoticeIds.has(notice.id)
            ),
        [dismissedNoticeIds, response.data]
    );

    /**
     * Requests local dismissal for this provider's lifetime without acknowledging on the server.
     *
     * Postconditions: Repeated dismissal retains the existing set; visibility updates on a later render.
     */
    const dismiss = useCallback(
        (noticeId: number) => {
            setDismissedNoticeIds(current => {
                if (current.has(noticeId)) {
                    return current;
                }
                return new Set(current).add(noticeId);
            });
        },
        []
    );

    /**
     * Acknowledges on the server before removing the notice from the current cached response.
     *
     * Postconditions: On successful completion, cache mutation has completed without revalidation.
     * Failures from either operation show an error notification and are caught; promise fulfillment
     * alone does not establish that acknowledgement succeeded.
     */
    const acknowledge = useCallback(
        async (noticeId: number) => {
            try {
                await acknowledgeNotice(noticeId);
                await response.mutate(
                    current => ({
                        ...current,
                        notices: current?.notices?.filter(notice => notice.id !== noticeId) ?? []
                    }),
                    { revalidate: false }
                );
            } catch {
                notifications.show({
                    color: "error",
                    title: "Unable to acknowledge notice",
                    message: "The notice could not be acknowledged. Please try again."
                });
            }
        },
        [acknowledgeNotice, response]
    );

    return (
        <NoticeContext.Provider
            value={{ notices, loading: response.loading, acknowledge, dismiss }}
        >
            {children}
        </NoticeContext.Provider>
    );
};

export default NoticeProvider;
